import { useState, useEffect, useCallback, useMemo } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import {
  TeamAvailabilityRow,
  AvailabilityOverrideRow,
  CalendarEventRow,
  DayAvailability,
  TimeSlot,
} from '@/types/calendar'
import {
  InterviewTeamConfig,
  InterviewTeamConfigRow,
  configRowToConfig,
} from '@/types/animatorApplication'

// ============================================================================
// useInterviewTeamConfig - Fetch team member IDs and interview settings
// ============================================================================

export function useInterviewTeamConfig(configKey: string = 'animator_interviews') {
  const [config, setConfig] = useState<InterviewTeamConfig | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchConfig = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { data, error: fetchError } = await (client as any)
        .from('interview_team_config')
        .select('*')
        .eq('config_key', configKey)
        .single()

      if (fetchError) throw fetchError

      setConfig(data ? configRowToConfig(data as InterviewTeamConfigRow) : null)
    } catch (err: any) {
      console.error('Error fetching interview config:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [configKey])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  return {
    config,
    isLoading,
    error,
    refetch: fetchConfig,
  }
}

// ============================================================================
// useMultiUserAvailableSlots - Find slots where ALL users are free
// ============================================================================

interface UseMultiUserAvailableSlotsOptions {
  userIds: string[]
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  duration?: number // minutes, defaults to 30
}

interface UserAvailabilityData {
  availability: TeamAvailabilityRow[]
  overrides: AvailabilityOverrideRow[]
  events: Pick<CalendarEventRow, 'start_time' | 'end_time'>[]
}

interface BookedInterview {
  scheduled_start: string
  scheduled_end: string
}

export function useMultiUserAvailableSlots(options: UseMultiUserAvailableSlotsOptions) {
  const { userIds, startDate, endDate, duration = 30 } = options

  const [dayAvailability, setDayAvailability] = useState<DayAvailability[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const calculateSlots = useCallback(async () => {
    if (!userIds.length || !startDate || !endDate) {
      setDayAvailability([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Fetch data for ALL users in parallel
      const userDataPromises = userIds.map(async (userId): Promise<UserAvailabilityData> => {
        const [availabilityResult, overridesResult, eventsResult] = await Promise.all([
          // Base weekly availability
          client
            .from('team_availability')
            .select('*')
            .eq('admin_user_id', userId)
            .eq('is_available', true)
            .returns<TeamAvailabilityRow[]>(),

          // Date-specific overrides
          client
            .from('availability_overrides')
            .select('*')
            .eq('admin_user_id', userId)
            .gte('override_date', startDate)
            .lte('override_date', endDate)
            .returns<AvailabilityOverrideRow[]>(),

          // Calendar events (blocking)
          client
            .from('calendar_events')
            .select('start_time, end_time')
            .eq('owner_id', userId)
            .gte('start_time', `${startDate}T00:00:00`)
            .lte('end_time', `${endDate}T23:59:59`)
            .returns<Pick<CalendarEventRow, 'start_time' | 'end_time'>[]>(),
        ])

        return {
          availability: availabilityResult.data || [],
          overrides: overridesResult.data || [],
          events: eventsResult.data || [],
        }
      })

      const allUserData = await Promise.all(userDataPromises)

      // Log each user's availability
      allUserData.forEach((userData, i) => {
        const userId = userIds[i]
        const availDays = userData.availability.map(a => `day${a.day_of_week}: ${a.start_time}-${a.end_time}`)
        console.log(`[MultiUserAvailability] User ${userId.slice(0, 8)}... availability:`, availDays.length ? availDays : 'NONE')
      })

      // Also fetch all scheduled animator applications to block those times
      // These are team-wide bookings that should block ALL team members
      const { data: bookedInterviews, error: bookedError } = await client
        .from('animator_applications')
        .select('scheduled_start, scheduled_end')
        .eq('status', 'scheduled')
        .gte('scheduled_start', `${startDate}T00:00:00`)
        .lte('scheduled_end', `${endDate}T23:59:59`)
        .returns<BookedInterview[]>()

      console.log('[MultiUserAvailability] Booked interviews query:', {
        startDate,
        endDate,
        bookedInterviews,
        bookedError,
        count: bookedInterviews?.length || 0,
      })

      // Log each booked interview's time range
      bookedInterviews?.forEach((interview, i) => {
        const start = new Date(interview.scheduled_start)
        const end = new Date(interview.scheduled_end)
        console.log(`[MultiUserAvailability] Interview ${i + 1}: ${start.toLocaleString()} - ${end.toLocaleString()}`)
      })

      const globalBlockedSlots = (bookedInterviews || []).map(interview => ({
        start: new Date(interview.scheduled_start),
        end: new Date(interview.scheduled_end),
      }))

      console.log('[MultiUserAvailability] Global blocked slots:', globalBlockedSlots)

      // Process each date in range
      const result: DayAvailability[] = []
      // Parse dates as local time, not UTC (YYYY-MM-DD strings are parsed as UTC by default)
      const [startY, startM, startD] = startDate.split('-').map(Number)
      const [endY, endM, endD] = endDate.split('-').map(Number)
      const current = new Date(startY, startM - 1, startD)
      const end = new Date(endY, endM - 1, endD)

      while (current <= end) {
        // Use local date format to avoid UTC date shift issues
        const y = current.getFullYear()
        const m = String(current.getMonth() + 1).padStart(2, '0')
        const d = String(current.getDate()).padStart(2, '0')
        const dateStr = `${y}-${m}-${d}`
        const dayOfWeek = current.getDay()

        // Calculate slots for each user
        const userSlots: TimeSlot[][] = allUserData.map((userData, _idx) => {
          return calculateUserSlotsForDay(
            userData,
            dateStr,
            dayOfWeek,
            duration,
            current,
            globalBlockedSlots
          )
        })

        // Find intersection - slots that are available for ALL users
        const intersectedSlots = findSlotIntersection(userSlots, duration)
        const availableCount = intersectedSlots.filter(s => s.available).length
        const isAvailable = availableCount > 0

        if (availableCount > 0 || intersectedSlots.length > 0) {
          console.log(`[MultiUserAvailability] Day ${dateStr} FINAL: ${intersectedSlots.length} intersected slots, ${availableCount} available, isAvailable=${isAvailable}`)
        }

        result.push({
          date: dateStr,
          isAvailable,
          slots: intersectedSlots,
        })

        current.setDate(current.getDate() + 1)
      }

      setDayAvailability(result)
    } catch (err: any) {
      console.error('Error calculating multi-user availability:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [userIds, startDate, endDate, duration])

  useEffect(() => {
    calculateSlots()
  }, [calculateSlots])

  // Days that have at least one available slot
  const availableDays = useMemo(() =>
    dayAvailability.filter(d => d.isAvailable),
    [dayAvailability]
  )

  return {
    dayAvailability,
    availableDays,
    isLoading,
    error,
    refetch: calculateSlots,
  }
}

// ============================================================================
// Helper: Calculate available slots for ONE user on ONE day
// ============================================================================

function calculateUserSlotsForDay(
  userData: UserAvailabilityData,
  dateStr: string,
  dayOfWeek: number,
  duration: number,
  currentDate: Date,
  globalBlockedSlots: { start: Date; end: Date }[] = []
): TimeSlot[] {
  const { availability, overrides, events } = userData

  // Check for date-specific overrides
  const dayOverrides = overrides.filter(o => o.override_date === dateStr)
  const unavailableOverride = dayOverrides.find(o => o.override_type === 'unavailable')

  // User is unavailable this entire day
  if (unavailableOverride) {
    return []
  }

  // Determine time ranges
  let ranges: { start: string; end: string }[] = []

  const customOverrides = dayOverrides.filter(o => o.override_type === 'custom')
  if (customOverrides.length > 0) {
    // Use custom hours
    ranges = customOverrides.map(o => ({
      start: o.start_time!,
      end: o.end_time!,
    }))
  } else {
    // Use base availability for this day of week
    // Note: day_of_week from DB might be string, dayOfWeek from JS is number
    const dayAvailability = availability.filter(a => Number(a.day_of_week) === dayOfWeek)

    if (availability.length > 0 && dayAvailability.length === 0) {
      console.log(`[MultiUserAvailability] Day ${dateStr} (dayOfWeek=${dayOfWeek}): No matching availability. DB days:`,
        availability.map(a => `${a.day_of_week} (${typeof a.day_of_week})`))
    }

    ranges = dayAvailability.map(a => ({
      start: a.start_time,
      end: a.end_time,
    }))
  }

  if (ranges.length === 0) {
    return []
  }

  console.log(`[MultiUserAvailability] Day ${dateStr}: Generating slots from ranges:`, ranges)

  // Get events for this specific day
  const dayEvents = events
    .filter(e => {
      const eventDate = new Date(e.start_time).toISOString().split('T')[0]
      return eventDate === dateStr
    })
    .map(e => ({
      start: new Date(e.start_time),
      end: new Date(e.end_time),
    }))

  // Get globally blocked slots for this specific day (booked interviews)
  // Use local date comparison (not UTC) to match how slots are generated
  const dayGlobalBlocked = globalBlockedSlots.filter(slot => {
    const y = slot.start.getFullYear()
    const m = String(slot.start.getMonth() + 1).padStart(2, '0')
    const d = String(slot.start.getDate()).padStart(2, '0')
    const slotLocalDate = `${y}-${m}-${d}`
    return slotLocalDate === dateStr
  })

  if (dayGlobalBlocked.length > 0) {
    console.log(`[MultiUserAvailability] Day ${dateStr} has ${dayGlobalBlocked.length} blocked interviews:`,
      dayGlobalBlocked.map(b => `${b.start.toLocaleTimeString()} - ${b.end.toLocaleTimeString()}`))
  }

  // Generate slots
  const slots: TimeSlot[] = []

  for (const range of ranges) {
    const [startHour, startMin] = range.start.split(':').map(Number)
    const [endHour, endMin] = range.end.split(':').map(Number)

    const rangeStart = new Date(currentDate)
    rangeStart.setHours(startHour, startMin, 0, 0)

    const rangeEnd = new Date(currentDate)
    rangeEnd.setHours(endHour, endMin, 0, 0)

    let slotStart = new Date(rangeStart)

    while (slotStart < rangeEnd) {
      const slotEnd = new Date(slotStart.getTime() + duration * 60 * 1000)
      if (slotEnd > rangeEnd) break

      // Check for conflicts with calendar events
      const hasEventConflict = dayEvents.some(event =>
        (slotStart < event.end && slotEnd > event.start)
      )

      // Check for conflicts with booked interviews
      const hasBookedConflict = dayGlobalBlocked.some(blocked =>
        (slotStart < blocked.end && slotEnd > blocked.start)
      )

      const isConflicting = hasEventConflict || hasBookedConflict

      slots.push({
        start: slotStart.toISOString(),
        end: slotEnd.toISOString(),
        available: !isConflicting,
      })

      slotStart = new Date(slotStart.getTime() + duration * 60 * 1000)
    }
  }

  const availableSlots = slots.filter(s => s.available)
  if (slots.length > 0) {
    console.log(`[MultiUserAvailability] Day ${dateStr}: Generated ${slots.length} slots, ${availableSlots.length} available`)
  }

  return slots
}

// ============================================================================
// Helper: Find intersection of slots across ALL users
// ============================================================================

function findSlotIntersection(userSlots: TimeSlot[][], _duration: number): TimeSlot[] {
  if (userSlots.length === 0) return []
  if (userSlots.length === 1) return userSlots[0]

  // Start with first user's slots
  const firstUserSlots = userSlots[0]

  // For each slot, check if ALL other users also have it available
  return firstUserSlots.map(slot => {
    // Only consider slots that are available for the first user
    if (!slot.available) {
      return { ...slot, available: false }
    }

    // Check if this slot time is available for ALL other users
    const slotStart = new Date(slot.start).getTime()
    const slotEnd = new Date(slot.end).getTime()

    const availableForAll = userSlots.slice(1).every(otherUserSlots => {
      // Find a matching slot in other user's availability
      return otherUserSlots.some(otherSlot => {
        if (!otherSlot.available) return false
        const otherStart = new Date(otherSlot.start).getTime()
        const otherEnd = new Date(otherSlot.end).getTime()
        // Slots must match exactly (same start and end time)
        return otherStart === slotStart && otherEnd === slotEnd
      })
    })

    return {
      ...slot,
      available: availableForAll,
    }
  }).filter(slot => slot.available) // Only return available slots
}

// ============================================================================
// useSlotsForDate - Get available slots for a specific date
// ============================================================================

export function useSlotsForDate(
  dayAvailability: DayAvailability[],
  selectedDate: string | null
): TimeSlot[] {
  return useMemo(() => {
    if (!selectedDate) return []
    const day = dayAvailability.find(d => d.date === selectedDate)
    return day?.slots.filter(s => s.available) || []
  }, [dayAvailability, selectedDate])
}
